import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  Chip,
  List,
  Link,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Prompt } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { formatCurrency } from 'src/utils/currency';
import { formatDateISO } from 'src/utils/date';
import { createAxiosInstance } from 'src/utils/axios';
import makeBreadcrumbs from 'src/utils/breadcrumbs';
import CircularLoader from 'src/components/CircularLoader';
import NoResults from 'src/components/NoResults';
import PageWithBreadCrumbs from 'src/components/PageWithBreadCrumbs';
import PurchaseOrderService from 'src/services/PurchaseOrderService';
import ProviderInfo from 'src/components/PurchaseOrderDetails/cards/ProviderInfo';
import ServiceInfo from 'src/components/PurchaseOrderDetails/cards/ServiceInfo';
import ChargeInfo from 'src/components/PurchaseOrderDetails/cards/ChargeInfo';
import NSDPayments from 'src/components/PurchaseOrderDetails/cards/NSDPayments';
import PolicyInfo from 'src/components/PurchaseOrderDetails/cards/PolicyInfo';
import NotesInfo from 'src/components/PurchaseOrderDetails/cards/NotesInfo';
import PoHistory from 'src/components/PurchaseOrderDetails/tables/PoHistory';
import Attachments from 'src/components/PurchaseOrderDetails/cards/Attachments';
import SupplementReasonDialog from 'src/components/PurchaseOrderDetails/modals/SupplementReasonDialog';
import RetailPaymentsInfo from 'src/components/PurchaseOrderDetails/cards/RetailPaymentsInfo';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
  },
  contentBody: {
    marginTop: '10px',
    height: '100%',
    [theme.breakpoints.up('lg')]: {
      width: 'calc(100vw - 330px)',
      paddingRight: '50px',
    },
  },
  changeHistory: {
    position: 'fixed',
    top: '70px',
    right: '10px',
    [theme.breakpoints.down('md')]: {
      right: '10px',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  noResultsContainer: {
    marginTop: '20%',
  },
  loadingContainer: {
    marginTop: '30%',
  },
  statusChips: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  chargeTotalDirty: {
    color: theme.palette.warning.main,
    fontWeight: theme.typography.fontWeightRegular,
  },
  purchaseOrderSubHeader: {
    fontWeight: theme.typography.fontWeightMedium,
    position: 'relative',
    top: '-20px',
  },
  purchaseOrderSubHeaderValue: {
    color: theme.palette.secondary.main,
    paddingLeft: '5px',
    paddingRight: '10px',
  },
  saveButton: {
    position: 'relative',
    top: '10px',
  },
  supplementButton: {
    position: 'relative',
    top: '10px',
    marginRight: '10px',
  },
  historyButton: {
    position: 'relative',
    top: '10px',
    marginRight: '10px',
  },
}));

const purchaseOrderService = new PurchaseOrderService(
  createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);

function PurchaseOrderDetail({ 
  purchaseOrderId,
  parentPurchaseOrderBaseUri,
  purchaseOrderBaseSearchUri,
  isAddNoteButtonDisabled,
  isChargeEditsDisabled,
  canUserAddCharge,
  retailPaymentDetailUri,
  includeNSDPayments,
  includeAttachments,
  includeRetailPayments,
 }) {
  const myRef = React.useRef(null);

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState(null);
  const [purchaseOrderHistory, setPurchaseOrderHistory] = useState(null);
  const [purchaseOrderAttachments, setPurchaseOrderAttachments] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [purchaseOrderChargeTypes, setPurchaseOrderChargeTypes] = useState(null);
  const [isError, setIsError] = useState(false);
  const [chipArray, setChipArray] = useState([]);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [isSupplementReasonsVisible, setIsSupplementReasonsVisible] = useState(false);
  const [purchaseOrderSupplementReasons, setPurchaseOrderSupplementReasons] = useState(null);
  const [purchaseOrderRetailPayments, setPurchaseOrderRetailPayments] = useState(null);

  useEffect(() => {
    getPurchaseOrderDetails(purchaseOrderId);
    getPurchaseOrderChargeTypes();
    if (includeRetailPayments){
      getPurchaseOrderRetailPaymentsDetails(purchaseOrderId);
    }
    if (includeAttachments){
      getPurchaseOrderAttachments(purchaseOrderId);
    }
  }, [purchaseOrderId]);

  async function getPurchaseOrderDetails(poId) {
    try {
      setIsLoading(true);
      setIsError(false);
      const res = await purchaseOrderService.getPurchaseOrderDetails(poId);
      res.data.data.isDirty = false;
      setPurchaseOrderDetails(res.data.data);
    } catch (e) {
      enqueueSnackbar('Error fetching purchase order details', {
        variant: 'error',
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function getPurchaseOrderAttachments(poId) {
    try {
      setIsLoading(true);
      setIsError(false);
      const res = await purchaseOrderService.getPurchaseOrderAttachments(poId);
      res.data.data.isDirty = false;
      setPurchaseOrderAttachments(res.data.data);
    } catch (e) {
      enqueueSnackbar('Error fetching purchase order attachments', {
        variant: 'error',
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function getPurchaseOrderHistory(poId) {
    try {
      setIsLoading(true);
      setIsError(false);
      const res = await purchaseOrderService.getPurchaseOrderHistory(poId);
      setPurchaseOrderHistory(res.data.data);
    } catch (e) {
      enqueueSnackbar('Error fetching purchase order history', {
        variant: 'error',
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function getSupplementReasons() {
    try {
      setIsLoading(true);
      setIsError(false);
      const res = await purchaseOrderService.getPurchaseOrderReasons('supplement');
      setPurchaseOrderSupplementReasons(res.data.data);
    } catch (e) {
      enqueueSnackbar('Error fetching purchase order history', {
        variant: 'error',
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function getPurchaseOrderRetailPaymentsDetails(poNumber) {
    try {
      if (poNumber){
        setIsLoading(true);
        setIsError(false);
        const res = await purchaseOrderService.getPurchaseOrderRetailPayments(poNumber);//Add to service
        setPurchaseOrderRetailPayments(res.data.data[0]);
      }
    } catch (e) {
      enqueueSnackbar('Error fetching purchase order retail payments details', {
        variant: 'error',
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleShowHistory() {
    await getPurchaseOrderHistory(purchaseOrderId);
    setIsHistoryVisible(true);
  }
  
  async function  handleInializeSupplement() {
    const po = {...purchaseOrderDetails, 
      Charges: [], 
      unloadedMileageAmount: 0,
      loadedMileageAmount: 0,
      TotalPurchaseOrderAmount:0,
      Attachments: [],
      StatusName: 'Approved',
      FreeLoadedMiles:0,
      MilesLoaded:0,
      FreeUnloadedMiles:0,
      MilesUnloaded:0,
      ParentPoNumber: purchaseOrderDetails.poNumber,
      isSupplement: true,
      IsPoEditable: true,
    };

    setPurchaseOrderDetails(po);
  }
  
  async function handleSelectSupplementReason() {
    await getSupplementReasons();
    setIsSupplementReasonsVisible(true);
  }

  async function handleCreateSupplementPurchaseOrder(reasonText) {
    try {
      const charges =  purchaseOrderDetails.Charges
      .map( o => ({
        purchaseOrderChargeId: Number(o.purchaseOrderChargeId),
        purchaseOrderChargeTypeId: Number(o.purchaseOrderChargeTypeId),
        amount: Number(o.amount),
      }));

      const res = await purchaseOrderService.createPurchaseOrderSupplement(
        purchaseOrderId, 
        reasonText,
        purchaseOrderDetails.FreeLoadedMiles??0,
        purchaseOrderDetails.LoadedMileageRate??0,
        purchaseOrderDetails.MilesLoaded??0,
        purchaseOrderDetails.FreeUnloadedMiles??0,
        purchaseOrderDetails.UnloadedMileageRate??0,
        purchaseOrderDetails.MilesUnloaded??0,
        charges);
      
      res.data.data.isDirty = false;
      setPurchaseOrderDetails(res.data.data);
      history.push(`${purchaseOrderBaseSearchUri}?id=${res.data.data.PoNumber}`);

  } catch (e) {
    enqueueSnackbar('Error create purchase order supplement', {
      variant: 'error',
    });
    setIsError(true);
  } finally {
    setIsLoading(false);
  }
 }

  async function getPurchaseOrderChargeTypes() {
    try {
      setIsLoading(true);
      setIsError(false);
      const res = await purchaseOrderService.getPurchaseOrderChargeTypes();
      setPurchaseOrderChargeTypes(res.data.purchaseOrderChargeTypes);
    } catch (e) {
      enqueueSnackbar('Error fetching purchase order charge types', {
        variant: 'error',
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  function isSaveEnabled() {
    if(!purchaseOrderDetails?.isSupplement)
      return purchaseOrderDetails?.isDirty;
    return purchaseOrderDetails?.TotalPurchaseOrderAmount >0;
  }
  
  async function handleCancelSupplement() {
    getPurchaseOrderDetails(purchaseOrderId);
    getPurchaseOrderChargeTypes();
    getPurchaseOrderAttachments(purchaseOrderId);
  }
  
  async function handleSavePurchaseOrder() {
    try {
      if(purchaseOrderDetails?.isSupplement) {
        handleSelectSupplementReason();
        return;
      }
      setIsLoading(true);
      setIsError(false);

      const charges =  purchaseOrderDetails.Charges
        .map( o => ({
          purchaseOrderChargeId: Number(o.purchaseOrderChargeId),
          purchaseOrderChargeTypeId: Number(o.purchaseOrderChargeTypeId),
          amount: Number(o.amount),
        }));
    
      const res = await purchaseOrderService.updatePurchaseOrder(
        purchaseOrderId, 
        purchaseOrderDetails.MilesUnloaded,
        purchaseOrderDetails.FreeUnloadedMiles,
        purchaseOrderDetails.UnloadedMileageRate,
        purchaseOrderDetails.MilesLoaded,
        purchaseOrderDetails.FreeLoadedMiles,
        purchaseOrderDetails.LoadedMileageRate,
        purchaseOrderDetails.StorageDays,
        purchaseOrderDetails.StorageDailyRate,
        purchaseOrderDetails.SecondaryStorageDays,
        purchaseOrderDetails.SecondaryStorageDailyRate,
        purchaseOrderDetails.CreditCardFeePercent,
        charges);

        res.data.data.isDirty = false;
        setPurchaseOrderDetails(res.data.data);
    } catch (e) {
      enqueueSnackbar('Error updating purchase order details', {
        variant: 'error',
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!purchaseOrderDetails) {
      return;
    }
    createChipArray(purchaseOrderDetails);
  }, [purchaseOrderDetails]);


  function handLoadedMileageClick() {
    myRef.current.updateLoadedMileage();
  }

  function handUnloadedMileageClick() {
    myRef.current.updateUnloadedMileage();
  }

  function createChipArray(PODetails) {
    const chipDataArray = [];
    if (PODetails.ProviderId) {
      chipDataArray.push({
        label: 'Provider Assigned',
        color: '#388e3c',
      });
    } else {
      chipDataArray.push({
        label: 'Provider Unassigned',
        color: '#f57c00',
      });
    }

    if (PODetails.ProviderCancelDate && !PODetails.IsGoa) {
      chipDataArray.push({
        label: 'Voided',
        color: '#f44336',
      });
    }

    if (PODetails.IsGoa) {
      chipDataArray.push({
        label: 'GOA',
        color: '#f44336',
      });
    }

    if (
      PODetails.ProviderAcceptDate
      && !PODetails.ProviderCancelDate
      && !PODetails.IsGoa
      && !PODetails.IsPaid
    ) {
      chipDataArray.push({
        label: 'Active',
        color: '#2196f3',
      });
    }

    if (PODetails.IsPaid) {
      chipDataArray.push({
        label: 'Paid',
        color: '#388e3c',
      });
    } else {
      chipDataArray.push({
        label: 'Not Paid',
        color: '#f57c00',
      });
    }

    if (PODetails.IsCashCall) {
      chipDataArray.push({
        label: 'Cash call',
        color: '#f57c00',
      });
    } else {
      chipDataArray.push({
        label: 'Not cash call',
        color: '#388e3c',
      });
    }

    if (PODetails.IsPartialPayment) {
      chipDataArray.push({
        label: 'Partial Payment',
        color: '#f57c00',
      });
    } 

    setChipArray(chipDataArray);
  }

  if (isError) {
    return (
      <div
        className={classes.noResultsContainer}
        title='Error getting details'
      >
        <NoResults />
      </div>
    );
  }

  const title = purchaseOrderDetails?.isSupplement ? `New Supplement PO (Original PO ${purchaseOrderId})` :
  purchaseOrderDetails?.ParentPoNumber ?  `PO ${purchaseOrderId} Details (Supplement of PO ${purchaseOrderDetails?.ParentPoNumber})`
  : `PO ${purchaseOrderId} Details`;

  const viewTitle = purchaseOrderDetails?.isSupplement ? 'New Supplement PO' : `PO ${purchaseOrderId} Details`;
  return (
    <main className={classes.root}>
      <PageWithBreadCrumbs
        title={title}
        breadcrumbs={makeBreadcrumbs(viewTitle)}
      >
        {isLoading ? (
          <div className={classes.loadingContainer}>
            <CircularLoader />
          </div>
        ) : (
          <>
            <Prompt
              when={purchaseOrderDetails && purchaseOrderDetails.isDirty}
              message="You have unsaved changes. Are you sure you want to leave?"
            />
            <div className={classes.purchaseOrderSubHeader}>
              <Grid container sx={{ position: 'relative' }}>
                <Grid item xs={4}>
                  <Typography variant='h5'>
                    Status:
                    <Typography variant="contained" className={classes.purchaseOrderSubHeaderValue}>
                      {purchaseOrderDetails?.StatusName}
                    </Typography>
                  </Typography>
                  {purchaseOrderDetails?.ParentPoNumber && (
                  <Typography variant='h5'>
                    Parent PO: 
                    <Link 
                      className={classes.purchaseOrderSubHeaderValue}
                      component={RouterLink}
                      to={parentPurchaseOrderBaseUri ? `${parentPurchaseOrderBaseUri}?id=${purchaseOrderDetails?.ParentPoNumber}`: `#`}
                    >
                      {purchaseOrderDetails?.ParentPoNumber}
                    </Link>
                  </Typography>
                )}
                </Grid>
                <Grid item xs={8} align="right">
                  <Typography variant='h5'>
                    {purchaseOrderDetails?.isDirty == true ? (
                      <>
                        PO Amount:
                        <span className={classes.chargeTotalDirty}>
                          {formatCurrency(purchaseOrderDetails?.TotalPurchaseOrderAmount ?? 0)}
                        </span>
                      </>
                    ) : (
                      <>
                        PO Amount:
                        <Typography variant="contained" className={classes.purchaseOrderSubHeaderValue}>
                          {formatCurrency(purchaseOrderDetails?.TotalPurchaseOrderAmount ?? 0)}
                        </Typography>
                      </>
                    )}
                  </Typography>
                  {!purchaseOrderDetails?.isSupplement &&
                  (
                    <Typography variant='body2' className={classes.changeHistory}>
                        Created:  
                      <Typography variant="contained" className={classes.purchaseOrderSubHeaderValue}> 
                        {formatDateISO(purchaseOrderDetails?.DateCreated)}
                      </Typography>
                      <Typography variant="contained" className={classes.purchaseOrderSubHeaderValue}>({purchaseOrderDetails?.CreatedBy??'System'})</Typography>
                        Modified:  
                      <Typography variant="contained" className={classes.purchaseOrderSubHeaderValue}> 
                        {formatDateISO(purchaseOrderDetails?.DateModified)}</Typography>
                      <Typography variant="contained" className={classes.purchaseOrderSubHeaderValue}>({purchaseOrderDetails?.ModifiedBy??'System'})</Typography>
                    </Typography>
                  )}

                  {isHistoryVisible ? 
                    <Button className={classes.historyButton} 
                      variant='contained' color='secondary' 
                      onClick={() => setIsHistoryVisible(false)}>Close History</Button>
                  : 
                    <>
                      {!purchaseOrderDetails?.isSupplement && <Button className={classes.historyButton} 
                        variant='contained' color='secondary' 
                        onClick={() => handleShowHistory()}>History</Button>}
                      {purchaseOrderDetails?.CanAddSupplement && !purchaseOrderDetails?.ParentPoNumber && !purchaseOrderDetails?.isSupplement && <Button className={classes.saveButton} 
                        variant='contained' color='primary' 
                        style={{marginRight:'10px'}}
                        onClick={() => handleInializeSupplement()}>Create Supplement</Button>}
                      {purchaseOrderDetails?.isSupplement && <Button className={classes.supplementButton} 
                        variant='contained' color='secondary' 
                        onClick={() => handleCancelSupplement()}>Cancel</Button>}
                      {purchaseOrderDetails?.IsPoEditable && <Button className={classes.saveButton} 
                        variant='contained' color='primary' 
                        disabled={!isSaveEnabled()}
                        onClick={() => handleSavePurchaseOrder()}>{!purchaseOrderDetails?.isSupplement ? 'Save' : 'Create PO'}</Button>}
                    </>
                  }
                </Grid>
              </Grid>
            </div>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <List className={classes.statusChips}>
                  {chipArray.map(item => (
                    <li key={item.label}>
                      <Chip
                        label={item.label}
                        style={{
                          color: 'white',
                          backgroundColor: item.color,
                          marginRight: 5,
                        }}
                      />
                    </li>
                  ))}
                </List>
              </Grid>
            </Grid>

            {isHistoryVisible &&
              <div className={classes.contentBody} >
                <Grid container spacing={2}>
                  <PoHistory purchaseOrderHistory={purchaseOrderHistory} />
                </Grid>
              </div>}

            {!isHistoryVisible && <div className={classes.contentBody} >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <ServiceInfo
                    customerName={purchaseOrderDetails?.CustomerName}
                    customerPhone={purchaseOrderDetails?.CustomerPhone}
                    dispatchId={purchaseOrderDetails?.DispatchId}
                    providerAcceptDate={purchaseOrderDetails?.ProviderAcceptDate}
                    providerCancelDate={purchaseOrderDetails?.providerCancelDate}
                    isGoa={purchaseOrderDetails?.IsGoa}
                    specialInstructions={purchaseOrderDetails?.SpecialInstructions}
                    requestedServiceType={purchaseOrderDetails?.RequestedServiceType}
                    jobType={purchaseOrderDetails?.JobType}
                    isCashCall={purchaseOrderDetails?.IsCashCall}
                    isPartialPayment={purchaseOrderDetails?.IsPartialPayment}
                    dispatchInternalNotes={purchaseOrderDetails?.InternalNotes}
                    dispatchProviderNotes={purchaseOrderDetails?.ProviderNotes}
                    dispatchDate={purchaseOrderDetails?.DispatchCreated}
                    dispatchState={purchaseOrderDetails?.DispatchState}
                    dispatchStatus={purchaseOrderDetails?.DispatchStatus}
                    loadedMileageRate={purchaseOrderDetails?.LoadedMileageRate}
                    freeLoadedMiles={purchaseOrderDetails?.FreeLoadedMiles}
                    milesLoaded={purchaseOrderDetails?.MilesLoaded}
                    unloadedMileageRate={purchaseOrderDetails?.UnloadedMileageRate}
                    freeUnloadedMiles={purchaseOrderDetails?.FreeUnloadedMiles}
                    milesUnloaded={purchaseOrderDetails?.MilesUnloaded}
                    unloadedMileageAmount={purchaseOrderDetails?.Charges?.find(o => o.purchaseOrderChargeTypeId === '4')?.amount ?? 0}
                    loadedMileageAmount={purchaseOrderDetails?.Charges?.find(o => o.purchaseOrderChargeTypeId === '3')?.amount ?? 0}
                    onLoadedMileageClick={handLoadedMileageClick}
                    onUnloadedMileageClick={handUnloadedMileageClick}
                    providerCategory={purchaseOrderDetails?.ProviderCategory}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <ProviderInfo
                    providerName={purchaseOrderDetails?.ProviderName}
                    providerPhone={purchaseOrderDetails?.ProviderPhone}
                    providerEmail={purchaseOrderDetails?.ProviderEmail}
                    providerHoursOfOperation={purchaseOrderDetails?.ProviderHoursOfOperation}
                    providerAddress={purchaseOrderDetails?.ProviderAddress}
                    providerCity={purchaseOrderDetails?.ProviderCity}
                    providerState={purchaseOrderDetails?.ProviderState}
                    providerCategory={purchaseOrderDetails?.ProviderCategory}
                    digitalProviderNetwork={purchaseOrderDetails?.DigitalProviderNetwork}
                    providerId={purchaseOrderDetails?.ProviderId}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                {includeRetailPayments && (
                  <RetailPaymentsInfo
                    paymentDate={purchaseOrderRetailPayments?.PaymentDate}
                    paymentAmount={purchaseOrderRetailPayments?.PaymentAmount}
                    paymentStatus={purchaseOrderRetailPayments?.PaymentStatus}
                    paymentStatusId={purchaseOrderRetailPayments?.PaymentStatusId}
                    refundedAmount={purchaseOrderRetailPayments?.RefundedAmount}
                    retailPaymentId={purchaseOrderRetailPayments?.RetailPaymentId}
                    stripePaymentIntentId={purchaseOrderRetailPayments?.StripePaymentId}
                    stripeInvoiceId={purchaseOrderRetailPayments?.StripeInvoiceId}
                    transactionId={purchaseOrderRetailPayments?.TransactionId}
                    routeToRetailPaymentDetail={retailPaymentDetailUri}
                  >
                  </RetailPaymentsInfo>
                )}
                  <PolicyInfo
                    memberNumber={purchaseOrderDetails?.MemberNumber}
                    agencyCode={purchaseOrderDetails?.AgencyCode}
                    policyNumber={purchaseOrderDetails?.PolicyNumber}
                    productNumber={purchaseOrderDetails?.ProductNumber}
                    planNumber={purchaseOrderDetails?.PlanNumber}
                    maxCoverageMiles={purchaseOrderDetails?.MaxCoverageMiles}
                    maxCoverageCosts={purchaseOrderDetails?.MaxCoverageCosts}

                  /> 
                  {includeNSDPayments && (
                  <NSDPayments
                    pOPaymentAmount={purchaseOrderDetails?.POPaymentAmount}
                  />)
                  }
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <ChargeInfo
                  ref={myRef}
                  chargeFees={purchaseOrderDetails?.Charges}
                  chargeTypes={purchaseOrderChargeTypes}
                  setPurchaseOrderDetails={setPurchaseOrderDetails}
                  purchaseOrderDetails={purchaseOrderDetails}
                  providerCategory={purchaseOrderDetails?.ProviderCategory}
                  isChargeEditsDisabled={isChargeEditsDisabled}
                  canUserAddCharge={canUserAddCharge}
                />
              </Grid>
              {includeAttachments &&  (
              <Grid item xs={12}> 
                <Attachments 
                  purchaseOrderId={purchaseOrderId}
                  purchaseOrderAttachments={purchaseOrderAttachments ?? []}
                  purchaseOrderService={purchaseOrderService}
                />
              </Grid>
              )}
              <Grid item xs={12}>
                <NotesInfo
                  poNumber={purchaseOrderDetails?.PoNumber}
                  dispatchId={purchaseOrderDetails?.DispatchId}
                  isAddNoteButtonDisabled={isAddNoteButtonDisabled}
                />
              </Grid>
            </div>}
          </>
        )}
         <SupplementReasonDialog
            isDialogOpen={isSupplementReasonsVisible}
            setIsDialogOpen={setIsSupplementReasonsVisible}
            handleApply={handleCreateSupplementPurchaseOrder}
            reasons={purchaseOrderSupplementReasons}
          /> 
      </PageWithBreadCrumbs>
    </main>
  );
}

PurchaseOrderDetail.propTypes = {
  purchaseOrderId: PropTypes.string,
  parentPurchaseOrderBaseUri: PropTypes.string,
  purchaseOrderBaseSearchUri: PropTypes.string.isRequired,
  isSaveInvoiceButtonDisabled: PropTypes.bool,
  isAddChargeButtonDisabled: PropTypes.bool,
  isAddNoteButtonDisabled: PropTypes.bool,
  isChargeEditsDisabled: PropTypes.bool,
  retailPaymentDetailUri: PropTypes.string.isRequired,
  includeNSDPayments: PropTypes.bool,
  includeAttachments: PropTypes.bool,
  includeRetailPayments: PropTypes.bool,
};

PurchaseOrderDetail.defaultProps = {
  isSaveInvoiceButtonDisabled: false,
  isAddChargeButtonDisabled: false,
  isAddNoteButtonDisabled: false,
  isChargeEditsDisabled: false,
  includeNSDPayments: true,
  includeAttachments: true,
  includeRetailPayments: true,
}

export default PurchaseOrderDetail;
